<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";

import {
  deleteWorkPack,
  getProject,
  listWorkPacks,
  listWorkPackTemplates,
  pushEvents,
} from "@/api/client";
import { WORK_PACK_STATUS_LABEL, type WorkPackRow, type WorkPackStatus } from "@/api/client";
import PageLayout from "@/components/PageLayout.vue";
import { useProjectStore } from "@/stores/project";
import { readiness } from "@/lib/workPackSections";

const queryClient = useQueryClient();
const { selectedProjectId } = storeToRefs(useProjectStore());
const { data, isPending, isError, error } = useQuery({
  queryKey: ["work-packs", selectedProjectId],
  queryFn: () => listWorkPacks(selectedProjectId.value),
});
const { data: templates } = useQuery({
  queryKey: ["work-pack-templates"],
  queryFn: () => listWorkPackTemplates(),
});

const refresh = (): Promise<void> =>
  queryClient.invalidateQueries({ queryKey: ["work-packs"] });

// --- create a work pack ---
// The pack is filed against the CURRENT project (from the URL), and the Week
// drives a sensible default Title + Reference.
const { data: project } = useQuery({
  queryKey: ["project", selectedProjectId],
  queryFn: () =>
    selectedProjectId.value
      ? getProject(selectedProjectId.value)
      : Promise.reject(new Error("No project")),
  enabled: () => !!selectedProjectId.value,
});
const today0 = new Date();
today0.setHours(0, 0, 0, 0);
const MS_WEEK = 7 * 86_400_000;

const week = ref("");
const title = ref("");
const reference = ref("");
const shiftDate = ref("");
const taskType = ref("");
const touched = reactive({ title: false, reference: false });
const creating = ref(false);
const formError = ref<string | null>(null);

const projectName = computed(() => project.value?.name ?? "");
const projectCode = computed(() => project.value?.code ?? "");

function weekNumOf(raw: string | null): number | null {
  const m = raw?.match(/\d+/);
  return m ? parseInt(m[0], 10) : null;
}

// ISO-8601 week number (Mon-start; the week containing the year's first
// Thursday is week 1) — matches how the office counts "this week".
function isoWeek(d: Date): number {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  t.setUTCDate(t.getUTCDate() - ((t.getUTCDay() + 6) % 7) + 3); // -> Thursday
  const firstThu = new Date(Date.UTC(t.getUTCFullYear(), 0, 4));
  firstThu.setUTCDate(firstThu.getUTCDate() - ((firstThu.getUTCDay() + 6) % 7) + 3);
  return 1 + Math.round((t.getTime() - firstThu.getTime()) / MS_WEEK);
}
function mondayOf(d: Date): Date {
  const t = new Date(d);
  t.setDate(t.getDate() - ((t.getDay() + 6) % 7));
  t.setHours(0, 0, 0, 0);
  return t;
}

const currentWeekNum = isoWeek(today0);
const currentMonday = mondayOf(today0);
// Week-commencing date for any week number, relative to the current week.
function dateForWeek(n: number): Date {
  return new Date(currentMonday.getTime() + (n - currentWeekNum) * MS_WEEK);
}
const defaultWeek = computed(() => `WK${String(currentWeekNum).padStart(2, "0")}`);

// The period (month + year, and week-commencing date) for whatever week is set.
const weekStart = computed(() => {
  const n = weekNumOf(week.value);
  return n != null ? dateForWeek(n) : null;
});
const monthYear = computed(() =>
  weekStart.value
    ? weekStart.value.toLocaleDateString("en-GB", { month: "short", year: "numeric" })
    : "",
);
const weekCommencing = computed(() =>
  weekStart.value
    ? `w/c ${weekStart.value.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`
    : "",
);

const suggestedTitle = computed(() => {
  if (!week.value || !projectName.value) return "";
  return monthYear.value
    ? `${week.value} ${monthYear.value} — ${projectName.value}`
    : `${week.value} — ${projectName.value}`;
});
const suggestedReference = computed(() => {
  if (!week.value) return "";
  const base = projectCode.value ? `${projectCode.value}-${week.value}` : week.value;
  return weekStart.value ? `${base}-${weekStart.value.getFullYear()}` : base;
});

watch(defaultWeek, (w) => { if (w && !week.value) week.value = w; }, { immediate: true });
watch(suggestedTitle, (t) => { if (!touched.title) title.value = t; }, { immediate: true });
watch(suggestedReference, (r) => { if (!touched.reference) reference.value = r; }, { immediate: true });

async function createPack(): Promise<void> {
  if (!selectedProjectId.value || !projectName.value) {
    formError.value = "No project selected.";
    return;
  }
  if (!title.value.trim()) {
    formError.value = "A title is required.";
    return;
  }
  creating.value = true;
  formError.value = null;
  try {
    await pushEvents([
      {
        aggregate_type: "work_pack",
        aggregate_id: crypto.randomUUID(),
        event_type: "WorkPackCreated",
        payload: {
          title: title.value.trim(),
          project_id: selectedProjectId.value,
          project_name: projectName.value,
          reference: reference.value.trim() || undefined,
          shift_date: shiftDate.value || undefined,
          task_type: taskType.value || undefined,
          created_at: new Date().toISOString(),
        },
      },
    ]);
    // Reset for the next pack; keep the week, re-suggest title/reference.
    touched.title = touched.reference = false;
    title.value = suggestedTitle.value;
    reference.value = suggestedReference.value;
    shiftDate.value = "";
    await refresh();
  } catch (e) {
    formError.value = e instanceof Error ? e.message : String(e);
  } finally {
    creating.value = false;
  }
}

const ready = (pack: Parameters<typeof readiness>[0]) =>
  readiness(pack, templates.value);

async function removePack(pack: WorkPackRow): Promise<void> {
  const warn =
    pack.status === "issued"
      ? `"${pack.title}" has been issued to site. Delete it?\n\nIt's removed from the list but the record (and any sign-on) is kept.`
      : `Delete "${pack.title}"?\n\nIt's removed from the list but the record is kept.`;
  if (!window.confirm(warn)) return;
  await deleteWorkPack(pack.id);
  await refresh();
}
</script>

<template>
  <PageLayout>
    <div class="grid gap-8">
      <header class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 class="text-2xl font-extrabold text-neutral-900">
            Work packs
          </h1>
          <p class="mt-1 text-neutral-500">
            Office-issued packs of shift documents — planned, verified and
            authorised here, received on site.
          </p>
        </div>
        <RouterLink
          :to="{ name: 'work-pack-map', params: { projectId: selectedProjectId } }"
          class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
        >
          View on map
        </RouterLink>
      </header>

      <!-- Create -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <h2 class="mb-1 font-semibold">
          New work pack
        </h2>
        <p class="mb-4 text-sm text-neutral-500">
          Filed against
          <span class="font-medium text-neutral-700">{{ projectName || "this project" }}</span><span
            v-if="projectCode && projectCode !== projectName"
            class="text-neutral-400"
          > · {{ projectCode }}</span>.
        </p>
        <div class="grid max-w-2xl gap-x-4 gap-y-3 sm:grid-cols-2">
          <label class="grid gap-1 text-sm sm:col-span-2">
            <span class="text-neutral-500">Task type <span class="text-neutral-400">(optional — sets the required documents)</span></span>
            <select
              v-model="taskType"
              class="rounded-md border border-neutral-300 px-3 py-2"
            >
              <option value="">
                None
              </option>
              <option
                v-for="t in templates"
                :key="t.task_type"
                :value="t.task_type"
              >
                {{ t.task_type }}
              </option>
            </select>
          </label>
          <label class="grid content-start gap-1 text-sm">
            <span class="text-neutral-500">Week</span>
            <input
              v-model="week"
              type="text"
              placeholder="WK24"
              class="rounded-md border border-neutral-300 px-3 py-2"
            >
            <span
              v-if="weekCommencing"
              class="text-xs text-neutral-400"
            >{{ weekCommencing }}</span>
          </label>
          <label class="grid content-start gap-1 text-sm">
            <span class="text-neutral-500">Shift date <span class="text-neutral-400">(optional)</span></span>
            <input
              v-model="shiftDate"
              type="date"
              class="rounded-md border border-neutral-300 px-3 py-2"
            >
          </label>
          <label class="grid gap-1 text-sm sm:col-span-2">
            <span class="text-neutral-500">Title</span>
            <input
              v-model="title"
              type="text"
              placeholder="WK24 Jun 2026 — Priestfield OLE"
              class="rounded-md border border-neutral-300 px-3 py-2"
              @input="touched.title = true"
            >
          </label>
          <label class="grid gap-1 text-sm sm:col-span-2">
            <span class="text-neutral-500">Reference</span>
            <input
              v-model="reference"
              type="text"
              placeholder="MMA-122-WK24-2026"
              class="rounded-md border border-neutral-300 px-3 py-2"
              @input="touched.reference = true"
            >
          </label>
        </div>
        <p
          v-if="formError"
          class="mt-3 text-sm text-semantic-danger"
        >
          {{ formError }}
        </p>
        <button
          type="button"
          class="mt-4 rounded-md bg-brand-primary px-4 py-2 font-semibold text-brand-primary-on transition hover:opacity-90 disabled:opacity-50"
          :disabled="creating || !projectName || !title.trim()"
          @click="createPack"
        >
          {{ creating ? "Creating…" : "Create work pack" }}
        </button>
      </section>

      <!-- List -->
      <div
        v-if="isPending"
        class="text-neutral-500"
      >
        Loading…
      </div>
      <div
        v-else-if="isError"
        class="rounded-md bg-semantic-danger/10 p-4 text-semantic-danger"
      >
        Failed to load: {{ error?.message }}
      </div>
      <div
        v-else-if="data"
        class="grid gap-4"
      >
        <p
          v-if="!data.length"
          class="text-neutral-500"
        >
          No work packs yet — create one above.
        </p>
        <RouterLink
          v-for="pack in data"
          :key="pack.id"
          :to="{ name: 'work-pack-detail', params: { projectId: selectedProjectId, id: pack.id } }"
          class="flex items-start justify-between gap-4 rounded-xl border border-neutral-200 bg-neutral-0 p-5 hover:bg-neutral-50"
        >
          <div>
            <h3 class="font-semibold text-neutral-900">
              {{ pack.title }}
            </h3>
            <p class="text-sm text-neutral-500">
              <span v-if="pack.reference">{{ pack.reference }} · </span>
              {{ pack.documents.length }} document{{ pack.documents.length === 1 ? "" : "s" }}
              <span v-if="pack.shift_date"> · shift {{ new Date(pack.shift_date).toLocaleDateString("en-GB") }}</span>
            </p>
          </div>
          <div class="flex shrink-0 items-center gap-2">
            <span
              v-if="pack.status !== 'issued'"
              class="rounded-full px-2.5 py-1 text-xs font-medium"
              :class="ready(pack).ready
                ? 'bg-semantic-success/10 text-semantic-success'
                : 'bg-semantic-warning/10 text-semantic-warning'"
            >
              {{ ready(pack).ready
                ? "Ready"
                : `${ready(pack).requiredPresent}/${ready(pack).requiredTotal} required` }}
            </span>
            <span
              class="rounded-full px-3 py-1 text-xs font-semibold"
              :class="pack.status === 'issued' || pack.status === 'authorised'
                ? 'bg-semantic-success/10 text-semantic-success'
                : pack.status === 'verified'
                  ? 'bg-brand-primary/10 text-brand-primary'
                  : 'bg-neutral-100 text-neutral-600'"
            >
              {{ WORK_PACK_STATUS_LABEL[pack.status as WorkPackStatus] ?? pack.status }}
            </span>
            <button
              type="button"
              class="rounded-md p-1 text-neutral-300 hover:bg-semantic-danger/10 hover:text-semantic-danger"
              title="Delete pack"
              @click.prevent.stop="removePack(pack)"
            >
              <svg
                class="h-4 w-4"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
            <svg
              class="h-4 w-4 text-neutral-300"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </RouterLink>
      </div>
    </div>
  </PageLayout>
</template>
